import { CalendarClock, Pencil, Plus, UserCheck, UserX } from 'lucide-react';
import * as React from 'react';
import type { Role } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Input, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { LostArt } from '@/components/illustrations';
import { Typewriter } from '@/components/typewriter';
import { ContractDialog } from '@/components/admin/contract-dialog';
import { EmployeeDialog } from '@/components/admin/employee-dialog';
import { TeamsPanel } from '@/components/admin/teams-panel';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError } from '@/lib/api';
import { ConfirmButton } from '@/components/confirm-dialog';
import { useSetEmployeeEnabled } from '@/lib/admin-api';
import { usePeople, type PersonRow } from '@/lib/people-api';
import type { Session } from '@/lib/session';

const ROLE_TONE: Record<string, 'default' | 'secondary' | 'outline'> = {
  ADMIN: 'default',
  HR: 'default',
  SUPERVISOR: 'secondary',
  EMPLOYEE: 'outline',
};

export function AdminPeoplePage({ session }: { session: Session }) {
  return (
    <PageContainer>
      <PageHeader
        title="People"
        description="Employees, working contracts and teams."
      />
      <Tabs defaultValue="people">
        <TabsList>
          <TabsTrigger value="people">Employees</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
        </TabsList>
        <TabsContent value="people"><EmployeeTable session={session} /></TabsContent>
        <TabsContent value="teams"><TeamsPanel /></TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function EmployeeTable({ session }: { session: Session }) {
  const [query, setQuery] = React.useState('');
  const [includeDisabled, setIncludeDisabled] = React.useState(false);
  const { data, isPending } = usePeople(includeDisabled);
  const setEnabled = useSetEmployeeEnabled();

  const [editing, setEditing] = React.useState<PersonRow | null | undefined>(undefined);
  const [contractFor, setContractFor] = React.useState<PersonRow | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const rows = React.useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data;
    // Filtered locally: 81 rows arrive at once, and a request per keystroke would
    // be slower than the filter.
    return data.filter(
      (p) =>
        p.displayName.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.jobTitle ?? '').toLowerCase().includes(q),
    );
  }, [data, query]);

  const toggleEnabled = (person: PersonRow) => {
    setError(null);
    setEnabled.mutate(
      { id: person.id, enabled: !person.enabled },
      {
        onError: (e) =>
          // The API refuses the last administrator and self-deactivation, and its
          // message says which - so it is shown rather than replaced.
          setError(e instanceof ApiError ? e.message : 'Could not change that.'),
      },
    );
  };

  if (isPending) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          {!query && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 truncate text-sm text-muted-foreground">
              <Typewriter text="Search by name, email or job title…" />
            </span>
          )}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search people by name, email or job title"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          Include people who have left
        </label>
        <span className="ts-num ml-auto text-sm text-muted-foreground">{rows.length} shown</span>
        <Button hint={"Add someone who signs in with their @innoctive.com Google account"} onClick={() => setEditing(null)}>
          <Plus className="size-4" />
          Add a person
        </Button>
      </div>

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Teams</TableHead>
                  <TableHead>Reports to</TableHead>
                  <TableHead>Last signed in</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((person) => (
                  <TableRow key={person.id} className={person.enabled ? undefined : 'ts-locked'}>
                    <TableCell>
                      <div className="font-medium">
                        {person.displayName}
                        {!person.enabled && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            left
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {person.email}
                        {person.jobTitle ? ` · ${person.jobTitle}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ROLE_TONE[person.role] ?? 'outline'}>
                        {person.role.toLowerCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {person.teams.length === 0
                        ? '—'
                        : person.teams.map((t) => (t.isLead ? `${t.name} (lead)` : t.name)).join(', ')}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {person.supervisor?.displayName ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      <LastSignIn at={person.lastLoginAt} method={person.lastLoginMethod} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon" hint={false}
                              aria-label="Working contract"
                              onClick={() => setContractFor(person)}
                            >
                              <CalendarClock className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Working contract</TooltipContent>
                        </Tooltip>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Edit person"
                          onClick={() => setEditing(person)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        {/* Deactivate, never delete: their hours are part of months
                            payroll has already settled. */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ConfirmButton
                              variant="ghost"
                              size="icon" hint={false}
                              aria-label={person.enabled ? 'Deactivate' : 'Reinstate'}
                              disabled={setEnabled.isPending}
                              pending={setEnabled.isPending}
                              title={
                                person.enabled
                                  ? `Deactivate ${person.displayName}?`
                                  : `Reinstate ${person.displayName}?`
                              }
                              description={
                                person.enabled ? (
                                  <>
                                    They will no longer be able to sign in, and they stop
                                    appearing in the pickers, the team report and the
                                    days-owed list. Everything they have already recorded is
                                    kept, and reinstating them restores access.
                                  </>
                                ) : (
                                  <>
                                    They will be able to sign in again and will reappear in
                                    the pickers and reports.
                                  </>
                                )
                              }
                              confirmLabel={person.enabled ? 'Deactivate' : 'Reinstate'}
                              destructive={person.enabled}
                              onConfirm={() => toggleEnabled(person)}
                            >
                              {person.enabled ? (
                                <UserX className="size-4" />
                              ) : (
                                <UserCheck className="size-4" />
                              )}
                            </ConfirmButton>
                          </TooltipTrigger>
                          <TooltipContent>
                            {person.enabled
                              ? 'Deactivate — their history is kept'
                              : 'Reinstate'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyState size="sm" art={LostArt} title={`Nobody matches “${query}”`} body={includeDisabled ? undefined : 'Someone who has left only shows with Include people who have left ticked.'}>
                        <Button variant="outline" size="sm" onClick={() => setQuery('')}>
                          Clear search
                        </Button>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {editing !== undefined && (
        <EmployeeDialog
          person={editing}
          people={data ?? []}
          actorRole={session.role as Role}
          actorId={session.id}
          onClose={() => setEditing(undefined)}
        />
      )}
      <ContractDialog employee={contractFor} onClose={() => setContractFor(null)} />
    </div>
  );
}

/**
 * When somebody last got in, and how.
 *
 * "Never" is a real and useful answer, not a missing value: every one of these
 * accounts came across from the previous system, so an account that has never
 * been used here is someone who has not made the move - which is a different
 * problem from someone who signed in months ago.
 *
 * The method is shown because it answers the question that decides when password
 * sign-in can be switched off: not "has anyone used Google" but "is anyone still
 * using a password".
 */
function LastSignIn({
  at,
  method,
}: {
  at: string | null;
  method: 'GOOGLE' | 'PASSWORD' | 'DEV' | null;
}) {
  if (!at) return <span className="text-xs text-muted-foreground">Never</span>;

  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86_400_000);
  const when =
    days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 30 ? `${days} days ago` : formatDay(at);

  return (
    <span className="flex flex-wrap items-baseline gap-1.5">
      <span className={days > 60 ? 'text-muted-foreground' : ''}>{when}</span>
      {method && (
        <Badge variant={method === 'PASSWORD' ? 'outline' : 'secondary'} className="font-normal">
          {method === 'GOOGLE' ? 'Google' : method === 'PASSWORD' ? 'password' : 'dev'}
        </Badge>
      )}
    </span>
  );
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
